import { useEffect, useState } from 'react'
import { authApi } from '../../api/client'
import { useAuthStore } from '../../store/authStore'

interface AuthBootstrapProps {
  children: React.ReactNode
}

/**
 * Waits for persisted auth rehydration, then validates session via GET /auth/me.
 */
export default function AuthBootstrap({ children }: AuthBootstrapProps) {
  const accessToken = useAuthStore((s) => s.accessToken)
  const updateUser = useAuthStore((s) => s.updateUser)
  const logout = useAuthStore((s) => s.logout)
  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated())
  const [sessionReady, setSessionReady] = useState(!accessToken)

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true))
    if (useAuthStore.persist.hasHydrated()) {
      setHydrated(true)
    }
    return unsub
  }, [])

  useEffect(() => {
    if (!hydrated) return

    if (!accessToken) {
      setSessionReady(true)
      return
    }

    let cancelled = false
    setSessionReady(false)

    authApi
      .getMe()
      .then((res) => {
        if (cancelled) return
        updateUser(res.data)
        setSessionReady(true)
      })
      .catch(() => {
        if (cancelled) return
        logout()
        setSessionReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [hydrated, accessToken, logout, updateUser])

  if (!hydrated || !sessionReady) {
    return null
  }

  return <>{children}</>
}
